import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { CONTACT_EMAIL, MAILTO } from "@/lib/contact";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "Creator Intelligence Tube respects your privacy. This policy explains what data we collect, how we use it, and the rights you have over your information.",
    sections: [
      { h: "Information we collect", p: "We collect only what's necessary to operate the service: analytics requests you make (public YouTube channel URLs and IDs), contact form submissions, and anonymous usage analytics to improve the product." },
      { h: "How we use your information", p: "To generate analyses, respond to your inquiries, and improve the platform. We never sell your data to third parties." },
      { h: "Data retention", p: "Analysis cache is retained for a rolling window to speed up repeat queries. Contact messages are stored for support history. You can request deletion at any time." },
      { h: "Third-party services", p: "We use the official YouTube Data API v3 for public channel data and OpenAI's GPT-5.2 (via Emergent) to generate AI diagnoses. Neither receives personally identifiable information from you." },
      { h: "Advertising", p: "We may use Google AdSense to serve ads on some pages. Google may use cookies to personalize ads based on your prior visits to this site or others. You can opt out of personalized advertising at https://www.google.com/settings/ads." },
      { h: "Your rights", p: "You have the right to access, correct, or delete your personal information. Contact us to exercise these rights." },
    ],
  },
  terms: {
    title: "Terms of Service",
    intro: "By using Creator Intelligence Tube, you agree to these terms. Please read them carefully.",
    sections: [
      { h: "Use of the service", p: "Creator Intelligence Tube is provided free of charge for analyzing publicly available YouTube channel data. You agree to use the service only for lawful purposes and not to abuse rate limits." },
      { h: "Estimates and disclaimers", p: "Revenue estimates and AI-generated recommendations are educational tools. Actual results depend on many factors outside our control. We provide no financial or performance guarantees." },
      { h: "Intellectual property", p: "Content displayed about a YouTube channel (thumbnails, titles, statistics) belongs to the respective creators and to YouTube. Our aggregation, layout and AI commentary are proprietary." },
      { h: "Limitation of liability", p: "The service is provided \"as is\" without warranties of any kind. We are not liable for indirect, incidental or consequential damages arising from use of the service." },
      { h: "Changes", p: "We may update these terms occasionally. Continued use of the service constitutes acceptance of the updated terms." },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro: "We use a minimal set of cookies and browser storage to run Creator Intelligence Tube smoothly.",
    sections: [
      { h: "What we use", p: "LocalStorage to remember your theme preference (light/dark). Optional anonymous analytics cookies for product improvement. No advertising cookies unless AdSense is enabled." },
      { h: "Managing preferences", p: "You can clear browser storage at any time from your browser's privacy settings. Doing so will reset your theme and any local preferences." },
      { h: "Third-party cookies", p: "Embedded YouTube thumbnails may originate cookies from Google's CDN when displayed. Google Analytics and AdSense may set cookies for measurement and advertising when enabled." },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    intro: "The information provided by Creator Intelligence Tube is for general informational and educational purposes only.",
    sections: [
      { h: "No affiliation with YouTube or Google", p: "Creator Intelligence Tube is an independent analytics platform and is not affiliated with, endorsed by, or sponsored by YouTube, LLC or Google LLC. All YouTube trademarks belong to their respective owners." },
      { h: "Accuracy of information", p: "All statistics are pulled from the official YouTube Data API v3 and reflect publicly available data at the time of the request. YouTube may update or remove data at any time." },
      { h: "Revenue estimates", p: "All revenue estimates are educated approximations based on public CPM benchmarks and the standard 55% creator share. Actual creator payouts vary greatly by niche, audience geography, ad blockers, and YouTube policies. Do not treat estimates as guaranteed earnings." },
      { h: "AI-generated content", p: "AI diagnoses are generated by large language models. While we work to keep them accurate and helpful, they may contain mistakes. Always apply your own judgment before acting on AI recommendations." },
      { h: "External links", p: "This site may contain links to external websites (e.g., YouTube channel pages). We are not responsible for the content or practices of external sites." },
    ],
  },
  support: {
    title: "Support",
    intro: "Need help with Creator Intelligence Tube? We're here to help — most questions are answered within one business day.",
    sections: [
      { h: "Fastest way to reach us", p: "Send us an email or use the Contact form. Please include the channel URL you're analyzing (if applicable) and a short description of what you're seeing." },
      { h: "Report a bug", p: "Include your browser, operating system, the URL you were on, and steps to reproduce. Screenshots are very helpful." },
      { h: "Feature request", p: "Tell us the problem you're trying to solve — not just the feature you want. We prioritize requests that help many creators at once." },
      { h: "API and partnerships", p: "For API access, integrations, or partnership inquiries, mention it in the subject line." },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const doc = CONTENT[slug];
  if (!doc) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-16">
        <h1 className="text-3xl font-bold">Not found</h1>
        <Link to="/" className="text-primary underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
      <SEO
        title={doc.title}
        description={`${doc.title} for Creator Intelligence Tube. ${doc.intro}`}
        path={`/legal/${slug}`}
      />
      <div className="overline">Legal</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">{doc.title}</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">{doc.intro}</p>

      <div className="mt-10 space-y-8">
        {doc.sections.map((s) => (
          <section key={s.h}>
            <h2 className="text-xl font-bold tracking-tight">{s.h}</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">{s.p}</p>
          </section>
        ))}
        <section className="card-flat p-6 mt-8">
          <h2 className="text-lg font-bold tracking-tight">Contact</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Questions about this policy? Email us at{" "}
            <a href={MAILTO} className="text-primary font-semibold hover:underline">{CONTACT_EMAIL}</a>
            {" "}or visit our <Link to="/contact" className="text-primary font-semibold hover:underline">Contact page</Link>.
          </p>
        </section>
      </div>
      <p className="mt-10 text-xs text-muted-foreground font-mono">
        Last updated: {new Date().toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" })}
      </p>
    </div>
  );
}
